import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Table, Modal } from "react-bootstrap";
import RequiredFieldMessage from "../common/userMessages/requiredFieldMessage";
import TaskComplete from "../common/taskComplete";
import userService from "../../services/userService";
import ReferencesForm from "../forms/pageForms/referencesForm";
import { getReferencesInputs } from "../forms/formEntities/referencesEntities";
import SaveAndContinueUserMessage from "../common/userMessages/saveAndContinueMessage";

export default function References({ user, userData, updatePage, shake }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const referencesInputs = getReferencesInputs(t);
  const [references, setReferences] = useState([]);
  const [showPageReqsMessage, setShowPageReqsMessage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handlePageReqs = (data) => {
    if (data && data.length >= 1) {
      setShowPageReqsMessage(false);
      return true;
    } else {
      setShowPageReqsMessage(true);
      shake();
      return false;
    }
  };

  const hasBothReferenceTypes = (data) => {
    const hasPersonal = data.some((ref) => ref.referenceType === "personal");
    const hasProfessional = data.some((ref) => ref.referenceType === "professional");
    return hasPersonal && hasProfessional;
  };

  const proceedToNextPage = async () => {
    setShowConfirmModal(false);
    setShowPageReqsMessage(false);
    const updatedReferenceInfo = {
      ...userData.referenceInfo,
      isComplete: references.length >= 2,
    };
    userData.referenceInfo = updatedReferenceInfo;
    updatePage();
    navigate("../phase2-education");
    await userService.saveUserData(user, "referenceInfo", updatedReferenceInfo);
  };

  const handleSubmitAndContinue = async () => {
    if (handlePageReqs(references)) {
      if (!hasBothReferenceTypes(references)) {
        setShowConfirmModal(true);
      } else {
        await proceedToNextPage();
      }
    }
  };

  // Assigning input names as keys for initialValues
  const inputKeys = referencesInputs.map((input) => {
    let keys = input.name;
    return keys;
  });

  // Creating initialValues by setting input keys to each have empty string value
  const initialValues = inputKeys.reduce((acc, key) => {
    acc[key] = "";
    acc.referenceType = "";
    return acc;
  }, {});

  const handleFormikSubmit = async (values, actions) => {
    Object.keys(values).forEach((key) => {
      if (values[key] === "" || !values[key]) {
        delete values[key];
      }
    });
    let newReferencesArray = [...references, { ...values }];

    const updatedReferenceInfo = {
      ...userData.referenceInfo,
      references: newReferencesArray,
      isComplete: newReferencesArray.length >= 2,
    };
    userData.referenceInfo = updatedReferenceInfo;
    await userService.saveUserData(user, "referenceInfo", updatedReferenceInfo);
    actions.resetForm();
    actions.resetForm(initialValues);

    setReferences(newReferencesArray);
    handlePageReqs(newReferencesArray);
    updatePage();
  };

  const handleReferenceDelete = async (index) => {
    references.splice(index, 1);
    setReferences(references);

    const updatedReferenceInfo = {
      ...userData.referenceInfo,
      references: references,
      isComplete: references.length >= 2,
    };
    userData.referenceInfo = updatedReferenceInfo;
    updatePage();

    await userService.saveUserData(user, "referenceInfo", updatedReferenceInfo);
  };

  useEffect(() => {
    if (userData.referenceInfo && userData.referenceInfo.references) {
      setReferences(userData.referenceInfo.references);
    }
  }, [userData]);

  return (
    <>
      {/* {userData.referenceInfo && userData.referenceInfo.isComplete ? (
        <section className="main-section">
          <h2>References</h2>
          <section className="main-section-content">
            <TaskComplete
              pageTitle={"References"}
              nextLink="phase2-education"
            />
          </section>
        </section>
      ) : (
        <> */}
      {/* Page Instructions  */}
      <section className="main-section">
        <h2>{t("references.title")}</h2>
        <section className="main-section-content text-start">
          <p>{t("references.intro1")}</p>
          <p>{t("references.intro2")}</p>
          {references && references.length === 0 && (
            <div className="alert alert-info" role="alert">
              {t("references.pageRequirements")}
            </div>
          )}
        </section>
      </section>

      {/* Form Section  */}
      <section className="main-section">
        <h2>{t("references.formTitle")}</h2>
        <div className="main-section-content">
          {/* Form Section - Instructions  */}
          <section className="text-start">
            <p className="text-start">{t("references.formInstructions1")}</p>
            <p className="text-start">{t("references.formInstructions2")}</p>
            <p>{t("references.requiredFieldIndicator")}</p>
          </section>
          <hr />

          {/* Form Section - Form  */}
          <section>
            <ReferencesForm
              shake={shake}
              handleFormikSubmit={handleFormikSubmit}
              initialValues={initialValues}
              updatePage={updatePage}
              userData={userData}
            />
          </section>
        </div>
      </section>

      {/* Review and Submit Section  */}
      <section className="main-section">
        <h2>{t("references.reviewTitle")}</h2>
        <section className="main-section-content">
          {/* Review and Submit Section -  Instructions  */}
          <section className="text-start">
            <p>{t("references.reviewInstructions1")}</p>
            <p>{t("references.reviewInstructions2")}</p>
          </section>

          {/* Review and Submit Section - Review Table  */}
          <section
            style={{ border: "1px solid black", marginBottom: "1rem" }}
          >
            {references.length > 0 ? (
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>{t("references.tableName")}</th>
                    <th>{t("references.tableEmail")}</th>
                    <th>{t("references.tablePhone")}</th>
                  </tr>
                </thead>
                <tbody>
                  {references.map((reference, index) => (
                    <tr key={index}>
                      <td>
                        <Button
                          variant="danger"
                          onClick={() => handleReferenceDelete(index)}
                          size="sm"
                          disabled={userData.submissionDate}
                        >
                          {t("references.delete")}
                        </Button>
                      </td>
                      <td>{reference.referenceName}</td>
                      <td>{reference.referenceEmail}</td>
                      <td>{reference.referencePhone}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="mb-0">{t("references.emptyReview")}</p>
            )}
          </section>

          {/* Review and Submit Section - Submit  */}
          <section>
            <RequiredFieldMessage
              handleClick={() => navigate("../phase2-education")}
              show={showPageReqsMessage ? true : false}
              reqMessage={t("references.minReferencesMessage")}
              hasButton={true}
            />
            <SaveAndContinueUserMessage />
            <Button variant="primary" onClick={handleSubmitAndContinue}>
              {t("common.saveAndContinue")}
            </Button>
          </section>
        </section>
      </section>
      {/* </>
      )} */}

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("references.confirmModalTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("references.confirmModalBody")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            {t("references.confirmModalNo")}
          </Button>
          <Button variant="primary" onClick={proceedToNextPage}>
            {t("common.yes")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
